import React, { FC } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import { useTranslation } from 'react-i18next';

import {
  ExpressActivatedUsersFragment,
  UpdatedExpressActivatedUser,
  useUpdateExpressActivatedUsersMutation,
} from 'api';
import { useNotify } from 'hooks';
import { UseCheckedState } from 'types/app';
import theme from 'theme';

interface Props extends Partial<UseCheckedState> {
  bulk?: boolean;
  count: number;
  variables: ExpressActivatedUsersFragment[];
  refetch: () => void;
}

const styles = {
  bulk: {
    borderRadius: theme.spacing(4),
    backgroundColor: theme.palette.secondary.main,
    paddingX: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  individual: {
    display: 'flex',
    alignItems: 'center',
    paddingX: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
} as const;

const ExpressActivatedUsersActions: FC<Props> = ({
  bulk,
  count,
  variables,
  resetCheckedState,
  hasChecked,
  refetch,
}) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const { mutate } = useUpdateExpressActivatedUsersMutation({
    onError: notify.mutationError,
    onSuccess: data => {
      notify.info({ message: getMessage(data.updateExpressActivatedUsers.updated_users) });
      refetch();
    },
  });

  function getMessage(users: UpdatedExpressActivatedUser[]) {
    return users[0].status === 'DENIED' ? t('invites:denySuccess') : t('invites:inviteSuccess');
  }

  function handleInvite() {
    const input = variables.map(variable => ({
      email: variable.email,
      status: 'COMPLETED',
    }));
    mutate({ users: input });
    resetCheckedState && resetCheckedState();
  }

  function handleDeny() {
    const input = variables.map(variable => ({
      email: variable.email,
      status: 'DENIED',
    }));
    mutate({ users: input });
    resetCheckedState && resetCheckedState();
  }

  function sendInvites() {
    return (
      <Tooltip title={t('invites:send')}>
        <IconButton onClick={handleInvite}>
          <ThumbUpRoundedIcon color={bulk ? 'primary' : 'inherit'} />
        </IconButton>
      </Tooltip>
    );
  }

  function denyUser() {
    return (
      <Tooltip title={t('invites:deny')}>
        <IconButton onClick={handleDeny}>
          <ThumbDownRoundedIcon color={bulk ? 'primary' : 'inherit'} />
        </IconButton>
      </Tooltip>
    );
  }

  if (bulk && !hasChecked) {
    return null;
  }

  return (
    <Box sx={bulk ? styles.bulk : styles.individual}>
      {bulk && (
        <Typography variant="body2" color="primary" paddingX={1}>
          {t('express:bulkSelected', { count })}
        </Typography>
      )}
      {sendInvites()}
      {denyUser()}
    </Box>
  );
};

ExpressActivatedUsersActions.defaultProps = {
  bulk: false,
};

export default ExpressActivatedUsersActions;
